import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { DragHandleIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import RemoveIconComponent from 'components/Icons/RemoveIcon';
import { getDashboardBlocks } from 'redux/DashboardSliceV2';
import DimensionTabIcon from 'components/Icons/ModelOverviewTabs/DimensionTabIcon';
import CustomToast from 'components/CustomToast';

export interface Properties {
  indicator: any;
  selectDimension: (dimension: any) => void;
  removeIndicator: (indicator_id: any) => void;
  showPopup: boolean;
}

const IndicatorRow = ({ indicator, selectDimension, removeIndicator, showPopup }: Properties) => {
  const [displayPopup, setDisplayPopup] = useState(false);
  const blockDetails = useSelector(getDashboardBlocks);
  const [hover, setHover] = useState<boolean>(false);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: indicator?.indicator_id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    zIndex: isDragging ? '100' : 'auto',
    opacity: isDragging ? 0.3 : 1,
  };
  const selectedBlock = useMemo(() => {
    if (indicator?.block_id) {
      const blockData = blockDetails?.find(
        (item: any) => item.block_id === Number(indicator.block_id),
      );
      return blockData?.block_name || 'Select block';
    }
    return null;
  }, [indicator?.block_id, blockDetails]);

  const blockIndicators = useMemo(() => {
    if (indicator?.block_id) {
      const blockData = blockDetails?.find(
        (item: any) => item.block_id === Number(indicator.block_id),
      );
      return blockData?.indicators || [] || [] || [];
    }
    return [];
  }, [indicator?.block_id, blockDetails]);

  const dimensions = useMemo(() => {
    if (indicator?.block_id && indicator?.indicator_id) {
      const blockData = blockDetails?.find(
        (item: any) => item.block_id === Number(indicator.block_id),
      );
      if (blockData) {
        const indicatorData = blockData?.indicators?.find(
          (item: any) => item.indicator_id === Number(indicator.indicator_id),
        );

        if (indicatorData && indicatorData?.indicator_dimensions?.length) {
          return indicatorData?.indicator_dimensions?.map((item: any) => ({
            dimension_id: item.indicator_dimension_id,
            dimension_name: item.indicator_dimension_name,
          }));
        }
      }
    }
    return [];
  }, [indicator?.block_id, indicator?.indicator_id, blockDetails]);

  const selectedDimensionName = useMemo(() => {
    if (indicator?.dimension_id && blockIndicators?.length) {
      const dimData = dimensions?.find(
        (item: any) => item.dimension_id === Number(indicator.dimension_id),
      );
      return dimData?.dimension_name;
    }
    return null;
  }, [indicator?.dimension_id, dimensions]);

  const selectedIndicatorName = useMemo(() => {
    if (indicator?.block_id && blockIndicators?.length) {
      const blockData = blockIndicators?.find(
        (item: any) => item.indicator_id === indicator.indicator_id,
      );
      return blockData?.indicator_name || 'Select indicator';
    }
    return null;
  }, [indicator?.indicator_id, blockIndicators]);

  const handleRemoveIndicator = (indicator_id: any) => {
    removeIndicator(indicator_id);
  };

  const handleDimensionSelect = (dim: any) => {
    if (!showPopup) {
      selectDimension(dim);
    } else {
      setDisplayPopup(true);
      setTimeout(() => {
        setDisplayPopup(false);
      }, 3000);
    }
  };

  return (
    <>
      <Box alignItems={'center'} ref={setNodeRef} style={style}>
        {indicator && (
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            onMouseOver={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <Flex
              display={'flex'}
              flexDirection={'row'}
              width={'100%'}
              justifyContent={'space-between'}
              paddingY={'8px'}
              bgColor={'#F5F5F5'}
              borderRadius={'7px'}
              pe={'3px'}
            >
              <Flex alignItems={'center'}>
                <DragHandleIcon
                  visibility={hover ? 'visible' : 'hidden'}
                  {...listeners}
                  {...attributes}
                  cursor={'grab'}
                  color={'#959595'}
                  _focus={{ outline: 'none' }}
                  height={'13px'}
                />
                <Box>
                  <Flex>
                    <Text
                      pl={1}
                      fontSize={'14px'}
                      textAlign={'left'}
                      textOverflow='ellipsis'
                      whiteSpace={'nowrap'}
                      overflow='hidden'
                      width='100px'
                    >
                      {selectedIndicatorName || 'Select indicator'}
                    </Text>
                  </Flex>
                  <Flex flexDirection={'column'} pl={1}>
                    {indicator?.block_id !== null && (
                      <Text fontSize={'10px'} color={'#787878'}>
                        {selectedBlock}
                      </Text>
                    )}
                  </Flex>
                </Box>
              </Flex>
              {dimensions?.length >= 1 && (
                <Menu strategy='fixed'>
                  <MenuButton>
                    <Flex alignItems='center' mr='5px'>
                      <DimensionTabIcon color={'#4BB6B9'} />
                      <Text fontSize={'14px'} color={'#4BB6B9'}>
                        {selectedDimensionName || '+ add'}
                      </Text>
                    </Flex>
                  </MenuButton>
                  <MenuList>
                    {dimensions?.map((dim: any) => {
                      return (
                        <MenuItem
                          key={dim?.dimension_id}
                          onClick={() => handleDimensionSelect(dim)}
                        >
                          {dim?.dimension_name}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              )}
            </Flex>
            <Flex alignItems={'center'} mx={1}>
              <RemoveIconComponent onClick={() => handleRemoveIndicator(indicator.indicator_id)} />
            </Flex>
          </Flex>
        )}
      </Box>
      {displayPopup && (
        <CustomToast
          title='Dimensions not available when displaying multiple indicators'
          status='error'
        />
      )}
    </>
  );
};
export default IndicatorRow;
